import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import Logo from "@/components/Logo";

const Header = () => {
  const { t, lang, toggle } = useLang();
  const { count, setOpen } = useCart();
  const [mobile, setMobile] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setMobile(false), [pathname]);

  const links = [
    { to: "/shop", label: t.nav.shop },
    { to: "/bundle", label: t.nav.bundle },
    { to: "/movement", label: t.nav.movement },
    { to: "/creators", label: t.nav.creators },
    { to: "/lookbook", label: t.nav.lookbook },
    { to: "/#social", label: t.nav.social },
  ];

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* Promo bar */}
      <div className="bg-pink text-cream border-b border-ink overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee py-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <span key={i} className="uppercase font-semibold text-[11px] tracking-[0.25em] px-6 flex-shrink-0">
              {t.announce}&nbsp;&nbsp;·&nbsp;&nbsp;{t.announce}&nbsp;&nbsp;·&nbsp;&nbsp;
            </span>
          ))}
        </div>
      </div>

      {/* Main bar */}
      <div className="bg-cream border-b border-ink">
        <nav className="max-w-[1440px] mx-auto px-4 md:px-8 h-16 md:h-[72px] flex items-center justify-between gap-4">
          <Logo className="text-xl md:text-2xl max-w-[160px] md:max-w-none" />

          <div className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="uppercase font-semibold text-sm tracking-[0.15em] text-ink hover:text-pink transition-colors duration-200"
              >
                {l.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={toggle}
              data-testid="lang-toggle"
              className="btn bg-cream text-ink h-9 px-4 text-xs hover:bg-ink hover:text-cream"
              aria-label="Toggle language"
            >
              {lang === "de" ? "EN" : "DE"}
            </button>

            <button
              onClick={() => setOpen(true)}
              data-testid="cart-button"
              className="relative btn bg-cream text-ink h-9 px-4 hover:bg-ink hover:text-cream"
              aria-label={t.nav.cart}
            >
              <ShoppingBag className="w-4 h-4" strokeWidth={2} />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-2 -right-2 bg-pink text-cream text-[11px] font-bold w-5 h-5 rounded-full flex items-center justify-center border border-ink"
                >
                  {count}
                </span>
              )}
            </button>

            <button
              onClick={() => setMobile((m) => !m)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden btn bg-cream text-ink h-9 w-9 p-0 hover:bg-ink hover:text-cream"
              aria-label="Menu"
            >
              {mobile ? <X className="w-4 h-4" strokeWidth={2} /> : <Menu className="w-4 h-4" strokeWidth={2} />}
            </button>
          </div>
        </nav>

        {mobile && (
          <div className="lg:hidden border-t border-ink bg-cream" data-testid="mobile-menu">
            <div className="flex flex-col px-6 py-4 divide-y divide-ink/10">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="display uppercase text-3xl py-3 text-ink hover:text-pink transition-colors"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
